// Importaciones de React y librerías.
import React from 'react';
import 'animate.css'; // Librería para las animaciones CSS.
import { FaLightbulb, FaChartLine, FaCog } from 'react-icons/fa'; // Iconos de la librería react-icons.
import { useInView } from 'react-intersection-observer'; // Hook para detectar la visibilidad de elementos.
import { Link } from 'react-router-dom'; // Para crear enlaces de navegación.

/**
 * Componente Valores.
 * Muestra una tarjeta con los "valores" o servicios que guían la aplicación de IA en una empresa.
 * Utiliza animaciones escalonadas para presentar cada punto de la lista.
 */
const Valores = () => {
  // Hook para detectar cuando el componente entra en el viewport.
  const [inViewRef, inView] = useInView({
    triggerOnce: true, // La animación solo se dispara una vez.
    threshold: 0.1,    // Se activa cuando al menos el 10% del componente es visible.
  });

  return (
    // Contenedor principal que centra la tarjeta.
    <div className="flex justify-center items-center min-h-screen text-black p-4">
      <div className="max-w-3xl bg-white rounded-lg shadow-2xl overflow-hidden transform transition-transform hover:scale-105 duration-300 ease-in-out">
        
        {/* Contenedor interno al que se le aplica la ref para la animación. */}
        <div 
          ref={inViewRef}
          // La clase de animación se aplica condicionalmente cuando `inView` es true.
          className={`p-8 rounded-lg shadow-md bg-gradient-to-r from-[#061C5B] to-[#203CE0] ${inView ? 'animate__animated animate__fadeInUp' : ''}`}
        >
          <h3 className="text-2xl font-bold text-white mb-6 text-center">Valores que guían la aplicación de IA en tu empresa:</h3>
          
          {/* Lista de valores/servicios. */}
          <ul className="space-y-6">
            
            {/* Cada `li` es un item de la lista con su propia animación escalonada. */}
            <li className={`flex items-start space-x-4 ${inView ? 'animate__animated animate__fadeInLeft' : ''}`}>
              <FaLightbulb className="text-4xl text-yellow-400 flex-shrink-0" />
              <div>
                <h4 className="text-xl font-semibold text-white">Taller de alineación estratégica en IA.</h4>
                <p className="text-white">Proporcionamos formación estratégica sobre la implementación de IA en su empresa.</p>
              </div>
            </li>

            {/* La clase `animate__delay-1s` retrasa la animación de este item por 1 segundo. */}
            <li className={`flex items-start space-x-4 ${inView ? 'animate__animated animate__fadeInLeft animate__delay-1s' : ''}`}>
              <FaChartLine className="text-4xl text-green-400 flex-shrink-0" />
              <div>
                <h4 className="text-xl font-semibold text-white">Presentación ejecutiva sobre IA.</h4>
                <p className="text-white">Ofrecemos presentaciones ejecutivas detalladas sobre las ventajas y aplicaciones de la IA.</p>
              </div>
            </li>

            {/* La clase `animate__delay-2s` retrasa la animación de este item por 2 segundos. */}
            <li className={`flex items-start space-x-4 ${inView ? 'animate__animated animate__fadeInLeft animate__delay-2s' : ''}`}>
              <FaCog className="text-4xl text-blue-400 flex-shrink-0" />
              <div>
                <h4 className="text-xl font-semibold text-white">Desarrollo de procesos y políticas para IA en tu empresa.</h4>
                <p className="text-white">Ayudamos a desarrollar procesos y políticas eficaces para integrar la IA en sus operaciones.</p>
              </div>
            </li>
          </ul>
          
          {/* Botón de llamada a la acción que enlaza a la página de consultas. */}
          <div className="text-center mt-8">
            <Link to={'/consultas'}>
              <button className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-700 transition-colors duration-300">
              Conversemos
              </button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

// Exporta el componente para ser usado en otras páginas.
export default Valores;
